import { Outlet, Navigate, Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthProvider";
import logo from "@/assets/logo4.png";
import i18n from "@/lib/i18n";

type Lang = "en" | "mr" | "hi";

const C = {
  gold: "#F97316",
  brown: "#C2410C",
  brownDark: "#7C2D12",
  cream: "#FFF7ED",
  warmWhite: "#FFFBF7",
  textMid: "#9A3412",
  textSoft: "#BE7A52",
  border: "rgba(249,115,22,0.18)",
};

const NAV_LABELS: Record<Lang, string[]> = {
  en: ["Features", "How It Works", "Contact"],
  mr: ["वैशिष्ट्ये", "कसे कार्य करते", "संपर्क"],
  hi: ["सुविधाएं", "यह कैसे काम करता है", "संपर्क"],
};

const NAV_HREFS = ["/#features", "/#how-it-works", "/contact"];

export default function AuthLayout() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const [lang, setLang] = useState<Lang>((i18n.language as Lang) || "mr");

  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  const links = NAV_LABELS[lang];

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: C.cream,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* ── NAV ── */}
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "rgba(255,247,237,0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${C.border}`,
          padding: "0 5%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 68,
        }}
      >
        <Link to="/">
          <img src={logo} alt="JaggeryBook" style={{ height: 44, objectFit: "contain", flexShrink: 0 }} />
        </Link>

        <ul className="hidden md:flex" style={{ gap: 28, listStyle: "none", margin: 0, padding: 0 }}>
          {links.map((link, i) => (
            <li key={i}>
              <a
                href={NAV_HREFS[i]}
                style={{ fontSize: 15, fontWeight: 500, color: C.textMid, textDecoration: "none" }}
              >
                {link}
              </a>
            </li>
          ))}
        </ul>

        {/* ── Desktop right section ── */}
        <div className="hidden md:flex" style={{ gap: 10, alignItems: "center" }}>
          <div style={{ background: C.cream, border: `1px solid ${C.border}`, borderRadius: 20, padding: 4, display: "flex", gap: 2 }}>
            {(["en", "mr", "hi"] as Lang[]).map((l) => (
              <button key={l} onClick={() => { setLang(l); i18n.changeLanguage(l); }} style={{ padding: "5px 12px", borderRadius: 16, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500, background: lang === l ? C.brown : "transparent", color: lang === l ? "#fff" : C.textSoft, transition: "all 0.2s" }}>
                {l === "en" ? "EN" : l === "mr" ? "मराठी" : "हिंदी"}
              </button>
            ))}
          </div>
          <Link to="/signin" style={{ fontSize: 14, fontWeight: 500, color: C.textMid, textDecoration: "none" }}>{t("auth.signIn")}</Link>
          <Link to="/signup" style={{ background: C.brown, color: "#fff", padding: "9px 20px", borderRadius: 8, fontSize: 14, fontWeight: 500, textDecoration: "none" }}>{t("auth.signUp")}</Link>
        </div>

        {/* ── Mobile right section: compact lang only ── */}
        <div className="flex md:hidden" style={{ alignItems: "center", gap: 6 }}>
          <div style={{ display: "flex", background: C.cream, border: `1px solid ${C.border}`, borderRadius: 16, padding: 3, gap: 2 }}>
            {(["en", "mr", "hi"] as Lang[]).map((l) => (
              <button key={l} onClick={() => { setLang(l); i18n.changeLanguage(l); }} style={{ padding: "4px 8px", borderRadius: 12, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, background: lang === l ? C.brown : "transparent", color: lang === l ? "#fff" : C.textSoft, transition: "all 0.2s" }}>
                {l === "en" ? "EN" : l === "mr" ? "मर" : "हि"}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* ── MAIN ── */}
      <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 5%" }}>
        <div style={{ width: "100%", maxWidth: 460 }}>
          <Outlet />
        </div>
      </main>

      {/* ── FOOTER ── */}
      <footer style={{ background: C.brownDark, padding: "20px 5%", textAlign: "center" }}>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", margin: 0 }}>
          © 2026 JaggeryBook. Built for jaggery manufacturers.
        </p>
      </footer>
    </div>
  );
}
